import React, {
  useState,
  useRef,
  useMemo,
  memo,
  useEffect,
  useContext,
  useCallback
} from 'react';
import classNames from 'classnames';
import { pdfjs, Document, Page } from 'react-pdf';
import VirtualList, { ListRef } from 'rc-virtual-list';
import {
  OverlayScrollbarsComponent,
  OverlayScrollbarsComponentRef
} from 'overlayscrollbars-react';
import { ReactComponent as SlideTextSvg } from '@assets/icons/slide-text.svg';
import type { fabric } from 'fabric';
import { setLatestZoomOptionFromStorage } from '@context/AssetViewerStateContext';
import PanelAction from '@pages/MediaViewer/AssetViewer/PanelAction';
import PlaybackPreparingLoader from '@pages/MediaViewer/AssetViewer/PlaybackPreparingLoader';
import { AssetVersionItemDto, DocumentMetadataDto } from '@api/Api';
import { FabricContext } from '@context/FabricContext';
import FabricCanvas from '@pages/MediaViewer/AssetViewer/Canvas/Canvas';
import PageThumbnail from '@pages/MediaViewer/AssetViewer/PdfCard/components/PageThumbnail';
import 'react-pdf/dist/Page/TextLayer.css';
import { highlightCreator } from '@pages/MediaViewer/AssetViewer/Canvas/Objects/highlightHandler';
import type { AssetViewerZoomOption } from '@context/AssetViewerStateContext/types';
import {
  getTwoFingerSpan,
  handleSelectionChange,
  pickMostVisiblePage
} from '@pages/MediaViewer/AssetViewer/PdfCard/utils';
import { useAssetViewerState } from '@hooks/useAssetViewerState';
import { useResponsive } from '@hooks/useResponsive';
import type { MediaViewerMobileActionVisibility } from '@pages/MediaViewer/components/MediaViewerMobileAction/MediaViewerMobileAction';
import MobilePdfPage from '@pages/MediaViewer/AssetViewer/PdfCard/components/MobilePdfPage';

import './PdfCard.less';

if (typeof (Promise as any).withResolvers === 'undefined') {
  (Promise as any).withResolvers = () => {
    let resolve;
    let reject;
    const promise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve, reject };
  };
}

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/legacy/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

// https://github.com/mozilla/pdf.js/blob/3b615e4ca369ff31e6399dea80383ffeffe96240/web/viewer_compatibility.js#L42-L48
const maxCanvasSize = 2048;

const minZoom = 0.1;
const maxZoom = 3;

function extractTextFromNode(node: Node): string {
  if (node.nodeName === 'BR') return '\n';
  if (node.nodeType === Node.TEXT_NODE) return node.textContent || '';
  if (node.nodeType === Node.ELEMENT_NODE)
    return Array.from(node.childNodes).map(extractTextFromNode).join('');
  return '';
}

interface IPdfCardProps {
  isExpand: boolean;
  setIsExpand(val: boolean): void;
  onReady: () => void;
  asset: AssetVersionItemDto;
  isPreparing: boolean;
  isAssetReady: boolean;
  mobileActionVisibility?: MediaViewerMobileActionVisibility | null;
}

export default memo(function PdfCard({
  isExpand,
  setIsExpand,
  onReady,
  asset,
  isPreparing,
  isAssetReady,
  mobileActionVisibility
}: IPdfCardProps) {
  const { isMdWidth } = useResponsive();
  const fabricContext = useContext(FabricContext);
  const {
    selectedCommentId,
    shapesDrawingTools,
    activePageNumber,
    zoomFactor,
    zoomOption,
    turnoverDegree,
    documentHighlight,
    selectComment,
    setActivePageNumber,
    setDocumentHighlight,
    setTurnoverDegree,
    setZoomFactor,
    setZoomOption,
    setCanvasMode
  } = useAssetViewerState();
  const isShapeDrawing = shapesDrawingTools.shape !== 'cursor';
  const documentHighlightRef = useRef(documentHighlight);
  const zoomRef = useRef(zoomFactor);
  zoomRef.current = zoomFactor;

  const pdfPinchGestureRef = useRef({
    isActive: false,
    startSpan: 0,
    baseZoom: 1
  });

  const listRef = useRef<ListRef>(null);
  const wrapperRef = useRef<OverlayScrollbarsComponentRef<'div'>>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const documentRef = useRef<HTMLDivElement>(null);
  const pageRefsMap = useRef(new Map<number, HTMLDivElement>());
  const skipActivePageScrollSyncRef = useRef(false);
  const activePageFromScrollRef = useRef(false);
  const isLoadPdf = useRef<boolean>(false);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const buttonSelectedTextRef = useRef<HTMLButtonElement>(null);

  const textLayersMapRef = useRef<Map<HTMLDivElement, HTMLDivElement>>(
    new Map()
  );

  const fileUrl = useMemo(() => {
    if (isPreparing) return null;
    const { proxy } = asset.metadata as DocumentMetadataDto;
    return proxy.url || '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset.id, isPreparing]);

  useEffect(() => {
    isLoadPdf.current = false;
    setIsPageSizeReady(false);
  }, [asset.id]);

  const [pagesCount, setPagesCount] = useState(1);

  const [isShowPages, setIsShowPages] = useState<boolean>(false);
  const [pageRefsVersion, setPageRefsVersion] = useState(0);
  const [pdfScrollViewport, setPdfScrollViewport] = useState<Element | null>(
    null
  );
  const [isPageSizeReady, setIsPageSizeReady] = useState(false);
  const [pageSize, setPageSize] = useState<{ width: number; height: number }>({
    width: 1605,
    height: 2272
  });

  const _pageSize = useMemo(() => {
    return {
      width:
        pageSize.width > pageSize.height
          ? pageSize.width * zoomFactor
          : undefined,
      height:
        pageSize.height > pageSize.width
          ? pageSize.height * zoomFactor
          : undefined
    };
  }, [pageSize, zoomFactor]);

  const pagesArray = useMemo(
    () =>
      new Array(pagesCount).fill('').map((_, index) => ({ page: index + 1 })),
    [pagesCount]
  );

  const onRegisterMobilePageRoot = useCallback(
    (page: number, element: HTMLDivElement | null) => {
      const hadElement = pageRefsMap.current.has(page);
      if (element) {
        pageRefsMap.current.set(page, element);
        if (!hadElement) setPageRefsVersion((value) => value + 1);
        return;
      }
      pageRefsMap.current.delete(page);
      if (hadElement) setPageRefsVersion((value) => value + 1);
    },
    []
  );

  const syncMobileFabricCanvas = useCallback(() => {
    const canvasContainer = canvasContainerRef?.current;
    const content = contentRef?.current;
    if (!canvasContainer || !content || !fabricContext.canvas) return;

    content.style.removeProperty('width');
    content.style.removeProperty('height');

    const pageEl = pageRefsMap.current.get(activePageNumber);
    const targetEl =
      (pageEl?.querySelector(
        '.media_viewer_pdf-inner'
      ) as HTMLElement | null) ??
      pageEl ??
      null;
    if (!targetEl) return;

    const rect = targetEl.getBoundingClientRect();
    const contentRect = content.getBoundingClientRect();
    const left = rect.left - contentRect.left + rect.width / 2;
    const top = rect.top - contentRect.top + rect.height / 2;

    canvasContainer.style.left = `${left}px`;
    canvasContainer.style.top = `${top}px`;
    canvasContainer.style.width = `${rect.width}px`;
    canvasContainer.style.height = `${rect.height}px`;
    canvasContainer.style.translate = `-${Math.round(
      rect.width / 2
    )}px -${Math.round(rect.height / 2)}px`;

    fabricContext.canvas.setWidth(rect.width);
    fabricContext.canvas.setHeight(rect.height);
    fabricContext.canvas.setViewportTransform([
      zoomFactor * 10,
      0,
      0,
      zoomFactor * 10,
      0,
      0
    ]);
  }, [
    activePageNumber,
    zoomFactor,
    fabricContext.canvas,
    turnoverDegree,
    pageRefsVersion
  ]);

  const updateActivePageFromScroll = useCallback(() => {
    if (!isMdWidth || skipActivePageScrollSyncRef.current) return;

    const viewport = wrapperRef.current?.osInstance()?.elements()?.viewport;
    if (!viewport) return;

    const nextPage = pickMostVisiblePage(
      pagesArray,
      pageRefsMap.current,
      viewport
    );
    if (!nextPage || nextPage === activePageNumber) return;

    activePageFromScrollRef.current = true;
    setActivePageNumber(nextPage);
    selectComment(null);
    setDocumentHighlight(null);
    setCanvasMode('view');
  }, [
    isMdWidth,
    pagesArray,
    activePageNumber,
    setActivePageNumber,
    selectComment,
    setDocumentHighlight,
    setCanvasMode
  ]);

  const onSelectText = (
    e: React.MouseEvent<HTMLButtonElement> | React.TouchEvent<HTMLButtonElement>
  ) => {
    e.preventDefault();
    setDocumentHighlight(documentHighlightRef.current);
  };

  const onClearHighlightObjects = useCallback(() => {
    const { canvas } = fabricContext;
    if (!canvas) return;
    const getHighlightObjects = canvas
      .getObjects()
      .filter((item) => item.type === 'highlight');
    getHighlightObjects.forEach((obj) => canvas.remove(obj));
    canvas.fire('object:history:remove');
  }, [fabricContext.canvas]);

  const renderTextHighlights = useCallback(
    (rects: any) => {
      if (!fabricContext.canvas) return;
      onClearHighlightObjects();
      highlightCreator({
        canvas: fabricContext.canvas,
        documentHighlightRects: rects,
        viewerActions: {
          selectComment,
          setCanvasMode,
          setDocumentHighlight
        }
      });
    },
    [fabricContext.canvas, onClearHighlightObjects]
  );

  useEffect(() => {
    if (!selectedCommentId) return;
    if (
      documentHighlightRef.current?.rects?.length &&
      !documentHighlight.rects?.length
    ) {
      onClearHighlightObjects();
    }
  }, [selectedCommentId, documentHighlight.rects, onClearHighlightObjects]);

  useEffect(() => {
    if (!isShapeDrawing) return;
    if (shapesDrawingTools.shape !== 'documentHighlight') return;
    const buttonSelectedText = buttonSelectedTextRef.current;
    const pageCanvas = pageCanvasRef.current;
    let prevRange: Range | null = null;

    const onSelectionchange = () => {
      const pageRoot = isMdWidth
        ? pageRefsMap.current.get(activePageNumber)
        : documentRef.current;
      const textLayerDiv = pageRoot?.querySelector('.textLayer');
      const endDiv = pageRoot?.querySelector('.endOfContent');
      if (!textLayerDiv) return;
      if (!endDiv) return;

      textLayersMapRef.current.set(
        textLayerDiv as HTMLDivElement,
        endDiv as HTMLDivElement
      );

      const range = handleSelectionChange(textLayersMapRef.current, prevRange);
      prevRange = range;

      if (buttonSelectedText) {
        buttonSelectedText.style.display = 'none';
        buttonSelectedText.style.pointerEvents = 'none';
      }

      if (!range || !pageCanvas) {
        onClearHighlightObjects();
        if (isMdWidth) setDocumentHighlight(null);
        return;
      }

      const rects = Array.from(range.getClientRects()).filter(
        (r) => r.width > 0 && r.height > 0 && r.top > 0
      );
      if (!rects.length) {
        onClearHighlightObjects();
        if (buttonSelectedText) {
          buttonSelectedText.style.display = 'none';
        }
        if (isMdWidth) setDocumentHighlight(null);
        return;
      }
      const canvasRect = pageCanvas.getBoundingClientRect();
      const selectedCoords = rects
        .map((rect) => ({
          left: ((rect.left - canvasRect.left) / canvasRect.width) * 100,
          top: ((rect.top - canvasRect.top) / canvasRect.height) * 100,
          width: (rect.width / canvasRect.width) * 100,
          height: (rect.height / canvasRect.height) * 100
        }))
        .filter((r) => r.left > 0 && r.top > 0);

      const fragment = range.cloneContents();
      documentHighlightRef.current = {
        text: Array.from(fragment.childNodes)
          .map(extractTextFromNode)
          .join('')
          .trim(),
        rects: selectedCoords
      };
      if (isMdWidth) {
        setDocumentHighlight(documentHighlightRef.current);
      }
      renderTextHighlights(selectedCoords);
      const containerRect = textLayerDiv.getBoundingClientRect();
      const lastRect = rects[selectedCoords.length - 1];
      const buttonWidth = buttonSelectedText?.offsetWidth || 0;
      const buttonHeight = buttonSelectedText?.offsetHeight || 0;
      const left = Math.min(
        Math.max(lastRect.right - containerRect.left + 8, 0),
        containerRect.width - buttonWidth
      );
      const top = Math.min(
        Math.max(lastRect.bottom - containerRect.top + 8, 0),
        containerRect.height - buttonHeight
      );
      if (buttonSelectedText) {
        buttonSelectedText.style.display = 'block';
        buttonSelectedText.style.left = `${left}px`;
        buttonSelectedText.style.top = `${top}px`;
      }
    };

    const onMouseUp = () => {
      if (!buttonSelectedText) return;
      buttonSelectedText.style.pointerEvents = 'auto';
    };

    const onTouchEnd = () => {
      if (!buttonSelectedText) return;
      buttonSelectedText.style.pointerEvents = 'auto';
    };

    document.addEventListener('selectionchange', onSelectionchange);
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('touchend', onTouchEnd);
    return () => {
      prevRange = null;
      textLayersMapRef.current.clear();
      if (isMdWidth) setDocumentHighlight(null);
      document.removeEventListener('selectionchange', onSelectionchange);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [
    zoomFactor,
    selectedCommentId,
    shapesDrawingTools,
    isShapeDrawing,
    fabricContext.canvas,
    documentHighlight?.rects,
    isMdWidth,
    activePageNumber
  ]);

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTo({
      index: activePageNumber - 1
    });
  }, [isShowPages, activePageNumber]);

  useEffect(() => {
    if (!isMdWidth) return;

    const viewport = wrapperRef.current?.osInstance()?.elements()?.viewport;
    if (!viewport) return;

    updateActivePageFromScroll();

    viewport.addEventListener('scroll', updateActivePageFromScroll, {
      passive: true
    });
    window.addEventListener('resize', updateActivePageFromScroll);

    return () => {
      viewport.removeEventListener('scroll', updateActivePageFromScroll);
      window.removeEventListener('resize', updateActivePageFromScroll);
    };
  }, [isMdWidth, updateActivePageFromScroll, pageRefsVersion]);

  useEffect(() => {
    if (!isMdWidth) return;
    updateActivePageFromScroll();
  }, [isMdWidth, zoomFactor, updateActivePageFromScroll]);

  useEffect(() => {
    if (!isMdWidth) {
      setPdfScrollViewport(null);
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      const viewport =
        wrapperRef.current?.osInstance()?.elements()?.viewport ?? null;
      setPdfScrollViewport(viewport);
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [isMdWidth, isAssetReady, pagesCount]);

  useEffect(() => {
    if (!isMdWidth || !pdfScrollViewport) return undefined;

    const pinch = pdfPinchGestureRef.current;

    const onTouchStart = (e: Event) => {
      const touchEvent = e as TouchEvent;
      if (touchEvent.touches.length < 2) return;
      const span = getTwoFingerSpan(touchEvent.touches);
      if (span < 10) return;
      pinch.isActive = true;
      pinch.startSpan = span;
      pinch.baseZoom = zoomRef.current;
    };

    const onTouchMove = (e: Event) => {
      const touchEvent = e as TouchEvent;
      if (!pinch.isActive || touchEvent.touches.length < 2) return;
      const span = getTwoFingerSpan(touchEvent.touches);
      if (pinch.startSpan < 10) return;
      const nextZoom = Math.min(
        maxZoom,
        Math.max(minZoom, pinch.baseZoom * (span / pinch.startSpan))
      );
      touchEvent.preventDefault();
      setZoomFactor(nextZoom);
    };

    const onTouchEnd = (e: Event) => {
      const touchEvent = e as TouchEvent;
      if (touchEvent.touches.length < 2) pinch.isActive = false;
    };

    const listenerCapturePassive = { capture: true, passive: true } as const;
    const listenerCaptureNotPassive = {
      capture: true,
      passive: false
    } as const;

    pdfScrollViewport.addEventListener(
      'touchstart',
      onTouchStart,
      listenerCapturePassive
    );
    pdfScrollViewport.addEventListener(
      'touchmove',
      onTouchMove,
      listenerCaptureNotPassive
    );
    pdfScrollViewport.addEventListener(
      'touchend',
      onTouchEnd,
      listenerCapturePassive
    );
    pdfScrollViewport.addEventListener(
      'touchcancel',
      onTouchEnd,
      listenerCapturePassive
    );

    return () => {
      pdfScrollViewport.removeEventListener(
        'touchstart',
        onTouchStart,
        listenerCapturePassive
      );
      pdfScrollViewport.removeEventListener(
        'touchmove',
        onTouchMove,
        listenerCaptureNotPassive
      );
      pdfScrollViewport.removeEventListener(
        'touchend',
        onTouchEnd,
        listenerCapturePassive
      );
      pdfScrollViewport.removeEventListener(
        'touchcancel',
        onTouchEnd,
        listenerCapturePassive
      );
    };
  }, [isMdWidth, pdfScrollViewport, setZoomFactor]);

  useEffect(() => {
    if (!isMdWidth) return;
    if (activePageFromScrollRef.current) {
      activePageFromScrollRef.current = false;
      return;
    }

    const pageEl = pageRefsMap.current.get(activePageNumber);
    if (!pageEl) return;

    skipActivePageScrollSyncRef.current = true;
    pageEl.scrollIntoView({ block: 'nearest' });
    const timeoutId = window.setTimeout(() => {
      skipActivePageScrollSyncRef.current = false;
    }, 300);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isMdWidth, activePageNumber]);

  useEffect(() => {
    if (isMdWidth) return;
    isLoadPdf.current = false;
    const viewport = wrapperRef.current?.osInstance()?.elements()?.viewport;
    viewport?.scroll({ top: 0, left: 0 });
  }, [isMdWidth, activePageNumber]);

  useEffect(() => {
    if (!selectedCommentId) return;
    if (selectedCommentId === 'new') return;
    const intId = setTimeout(() => {
      const getObjects = fabricContext.getObjects() || [];
      const viewport = wrapperRef.current?.osInstance()?.elements()?.viewport;
      if (!getObjects.length || !viewport) return;
      const firstObject: fabric.Object = getObjects[0];
      const objectCoord = firstObject.getCoords()[0];
      const coord = {
        x: objectCoord.x - firstObject.getScaledWidth(),
        y: objectCoord.y - firstObject.getScaledHeight()
      };
      const isInViewport =
        coord.x >= viewport.scrollLeft &&
        coord.x <= viewport.clientWidth &&
        coord.y >= viewport.scrollTop &&
        coord.y <= viewport.scrollTop + viewport.clientHeight;
      if (!isInViewport) viewport.scroll({ top: coord.y, left: coord.x });
    }, 250);
    return () => {
      clearTimeout(intId);
    };
  }, [selectedCommentId, documentHighlight.rects]);

  const getZoomFactorFromOption = useCallback(
    (option: NonNullable<AssetViewerZoomOption>) => {
      if (option === 'actual-size') return 1;
      const container = wrapperRef?.current?.getElement();
      if (!container || !isPageSizeReady) return 1;
      const width =
        Math.abs(pageSize.width * Math.cos(turnoverDegree * (Math.PI / 180))) +
        Math.abs(pageSize.height * Math.sin(turnoverDegree * (Math.PI / 180)));
      const height =
        Math.abs(pageSize.width * Math.sin(turnoverDegree * (Math.PI / 180))) +
        Math.abs(pageSize.height * Math.cos(turnoverDegree * (Math.PI / 180)));
      const containerWidth = container.offsetWidth;
      const containerHeight = container.offsetHeight;
      const scaleWidth = Math.floor(containerWidth / (width / 100)) / 100;
      const scaleHeight = Math.floor(containerHeight / (height / 100)) / 100;
      if (option === 'auto' || option === 'fit-width') {
        return scaleWidth;
      }
      if (option === 'fit-height') {
        return scaleHeight;
      }
      return 1;
    },
    [pageSize, turnoverDegree, isPageSizeReady]
  );

  const setZoom = useCallback(
    (v: number | NonNullable<AssetViewerZoomOption>) => {
      if (typeof v === 'number') {
        setZoomFactor(v);
      } else {
        const newZoomFactor = getZoomFactorFromOption(v);
        setZoomFactor(newZoomFactor, v);
      }
    },
    [getZoomFactorFromOption, setZoomFactor]
  );

  useEffect(() => {
    setZoomOption(isMdWidth ? 'fit-width' : setLatestZoomOptionFromStorage());
  }, [setZoomOption, isMdWidth]);

  useEffect(() => {
    if (!zoomOption || !isPageSizeReady) return undefined;

    const container = wrapperRef.current?.getElement();
    if (!container) return undefined;

    const applyZoom = () => {
      const newZoomFactor = getZoomFactorFromOption(zoomOption);
      setZoomFactor(newZoomFactor, zoomOption);
    };

    applyZoom();
    const observer = new ResizeObserver(applyZoom);
    observer.observe(container);
    return () => observer.disconnect();
  }, [zoomOption, getZoomFactorFromOption, setZoomFactor, isPageSizeReady]);

  useEffect(() => {
    if (isMdWidth) return;
    const documentEl = documentRef?.current;
    const content = contentRef?.current;
    const canvasContainer = canvasContainerRef?.current;
    if (!documentEl || !canvasContainer || !content || !fabricContext.canvas)
      return;

    canvasContainer.style.removeProperty('left');
    canvasContainer.style.removeProperty('top');

    const rect = documentEl.getBoundingClientRect();
    canvasContainer.style.width = `${rect.width}px`;
    canvasContainer.style.height = `${rect.height}px`;
    canvasContainer.style.translate = `-${Math.round(
      rect.width / 2
    )}px -${Math.round(rect.height / 2)}px`;
    content.style.width = `${rect.width}px`;
    content.style.height = `${rect.height}px`;
    fabricContext.canvas.setWidth(rect.width);
    fabricContext.canvas.setHeight(rect.height);
    fabricContext.canvas.setViewportTransform([
      zoomFactor * 10,
      0,
      0,
      zoomFactor * 10,
      0,
      0
    ]);
  }, [isMdWidth, pageSize, zoomFactor, fabricContext.canvas, turnoverDegree]);

  useEffect(() => {
    if (!isMdWidth) return undefined;

    syncMobileFabricCanvas();

    const viewport = wrapperRef.current?.osInstance()?.elements()?.viewport;
    viewport?.addEventListener('scroll', syncMobileFabricCanvas, {
      passive: true
    });
    window.addEventListener('resize', syncMobileFabricCanvas);

    return () => {
      viewport?.removeEventListener('scroll', syncMobileFabricCanvas);
      window.removeEventListener('resize', syncMobileFabricCanvas);
    };
  }, [syncMobileFabricCanvas, isMdWidth]);

  const onResetPosition = useCallback(() => {
    isLoadPdf.current = false;
  }, []);

  const onChangePage = useCallback(
    (page: number) => {
      onResetPosition();
      setActivePageNumber(page);
      selectComment(null);
      setDocumentHighlight(null);
      setCanvasMode('view');
    },
    [
      onResetPosition,
      setActivePageNumber,
      selectComment,
      setDocumentHighlight,
      setCanvasMode
    ]
  );

  const pageCanvasRef = useRef<HTMLCanvasElement>(null);
  const onLoadSuccess = useCallback(
    (pdfPage: { originalWidth: number; originalHeight: number }) => {
      if (isLoadPdf.current) return;
      isLoadPdf.current = true;
      const { originalWidth, originalHeight } = pdfPage;
      const scale = Math.min(
        maxCanvasSize / originalWidth,
        maxCanvasSize / originalHeight
      );
      setPageSize({
        width: originalWidth * scale,
        height: originalHeight * scale
      });
      setIsPageSizeReady(true);
    },
    [setPageSize]
  );

  const onToggleShowPages = useCallback(() => {
    setIsShowPages((prevIsShowPages) => !prevIsShowPages);
  }, [setIsShowPages]);

  const showPanelAction =
    !mobileActionVisibility || mobileActionVisibility.showPanelAction;

  const panelActionConfig = useMemo(
    () => ({
      zoom: zoomFactor,
      zoomOption,
      setZoom,
      isShapeDrawingToolsOpen: isShapeDrawing,
      isExpand,
      activePage: activePageNumber,
      pagesCount,
      onChangePage,
      onToggleShowPages,
      setIsExpand: (v: boolean) => {
        onResetPosition();
        setIsExpand(v);
      },
      setRotate: (v: number) => {
        setTurnoverDegree(turnoverDegree + v === 360 ? 0 : turnoverDegree + v);
      }
    }),
    [
      zoomFactor,
      zoomOption,
      setZoom,
      isShapeDrawing,
      isExpand,
      activePageNumber,
      pagesCount,
      onChangePage,
      onToggleShowPages,
      onResetPosition,
      setIsExpand,
      turnoverDegree,
      setTurnoverDegree
    ]
  );

  if (isPreparing) return <PlaybackPreparingLoader />;

  return (
    <Document
      className={classNames('media_viewer_pdf', {
        'b-hide-shapes-drawing':
          shapesDrawingTools?.shape === 'documentHighlight' && isShapeDrawing
      })}
      file={fileUrl}
      onLoadSuccess={(pdf) => {
        setPagesCount(pdf.numPages);
        onReady();
      }}
      loading={<PlaybackPreparingLoader />}
    >
      {isShowPages && (
        <div className="media_viewer_pdf-sidebar">
          <VirtualList
            className="media_viewer_pdf-list"
            data={pagesArray}
            height={wrapperRef?.current?.getElement()?.offsetHeight || 500}
            fullHeight={true}
            itemKey="page"
            ref={listRef}
          >
            {(item) => {
              return (
                <PageThumbnail
                  page={item.page}
                  active={item.page === activePageNumber}
                  maxWidth={102}
                  maxHeight={147}
                  onClick={onChangePage}
                />
              );
            }}
          </VirtualList>
        </div>
      )}
      {showPanelAction && !isMdWidth && <PanelAction {...panelActionConfig} />}
      {isMdWidth && (
        <div className="media_viewer_pdf-page-indicator">
          {activePageNumber} of {pagesCount}
        </div>
      )}
      <OverlayScrollbarsComponent
        className="media_viewer_pdf-wrapper"
        ref={wrapperRef}
      >
        <div
          className={classNames('media_viewer_pdf-content', {
            'media_viewer_pdf-content-mobile': isMdWidth
          })}
          ref={contentRef}
        >
          {isMdWidth && (
            <div className="media_viewer_pdf-mobile-pages">
              {pagesArray.map(({ page }) => (
                <MobilePdfPage
                  key={page}
                  pageNumber={page}
                  pageWidth={_pageSize.width}
                  pageHeight={_pageSize.height}
                  turnoverDegree={turnoverDegree}
                  isActive={page === activePageNumber}
                  isFirstPage={page === 1}
                  pageCanvasRef={pageCanvasRef}
                  onRegisterPageRoot={onRegisterMobilePageRoot}
                  onFirstPageLoadSuccess={onLoadSuccess}
                  intersectionRoot={pdfScrollViewport}
                />
              ))}
            </div>
          )}
          {!isMdWidth && (
            <div
              className="media_viewer_pdf-inner"
              ref={documentRef}
              style={{
                transform: `rotate(${turnoverDegree}deg)`,
                ..._pageSize
              }}
            >
              <Page
                width={_pageSize.width}
                height={_pageSize.height}
                scale={1}
                pageNumber={activePageNumber}
                className="media_viewer_pdf-page"
                canvasRef={pageCanvasRef}
                renderAnnotationLayer={false}
                onLoadSuccess={onLoadSuccess}
                loading={<PlaybackPreparingLoader />}
              />
            </div>
          )}
          {!isMdWidth && (
            <button
              ref={buttonSelectedTextRef}
              className="media_viewer_pdf-selected-btn"
              type="button"
              onClick={onSelectText}
              onTouchStart={onSelectText}
              style={{ display: 'none' }}
            >
              <SlideTextSvg />
            </button>
          )}
          <div ref={canvasContainerRef} className="media_viewer_pdf-canvas">
            <FabricCanvas />
          </div>
        </div>
      </OverlayScrollbarsComponent>
      {!isAssetReady && <PlaybackPreparingLoader />}
    </Document>
  );
});
